import type { CardParticipant } from "@/types/thread.ts";
import { Link } from "wouter";
import { cn } from "../../lib/utils.ts";
import { AvatarWithFallback } from "../ui/avatar.tsx";

type CardFacepileProps = {
  participants: CardParticipant[];
  /** Number of additional participants beyond those shown as avatars. */
  additional?: number;
  /**
   * Canonical URL for the thread's participants. Renders the facepile as a real
   * link, so the nav boundary picks the surface and cmd/middle-click still opens
   * the page. Preferred over `onClick` wherever the facepile isn't already
   * inside an anchor.
   */
  href?: string;
  /**
   * Click handler for hosts that cannot nest an anchor — a row or card whose
   * whole body is already a `<Link>`, or a menu item that owns the click. These
   * navigate imperatively to the same canonical URL instead; the boundary
   * intercepts that just the same. Ignored when `href` is set.
   */
  onClick?: () => void;
  /** Keeps the flex layout predictable when the facepile isn't full width. */
  className?: string;
};

/**
 * Compact avatar stack for the thread card + thread detail header.
 *
 * Visual treatment (matches the home-card spec from `frank/homeCard`):
 * - 20px (`size="xs"`) avatars overlap by 14px (`-ml-3.5`) so a 2-up stack
 *   reads as a tight pair, not two separate badges.
 * - When there are more participants than shown as avatars, the whole
 *   container becomes a soft `bg-accent` chip and the count appears as
 *   inline `+N` text — not a disc-shaped sibling that would inflate the
 *   facepile's footprint.
 * - Stacked avatars get `z-20` / `z-10` so the leftmost stays on top, and
 *   `border-popover` so the overlap reads cleanly against the card.
 * - `hover:opacity-70` is the standard click affordance for clickable
 *   avatar groups elsewhere in the app.
 *
 * Clicking anywhere reaches the thread's participant list. Hosts that can carry
 * an anchor pass `href` and get a real link; hosts already inside one — a row or
 * card whose whole body is a `<Link>` — pass `onClick` and navigate to the same
 * URL imperatively, since nesting anchors is invalid.
 *
 * With neither it renders as a static indicator rather than a button, so it
 * can sit inside a control that is itself interactive without nesting one
 * focusable element in another.
 */
export const CardFacepile = ({
  participants,
  additional = 0,
  href,
  onClick,
  className,
}: CardFacepileProps) => {
  if (participants.length === 0 && additional === 0) return null;

  const isInteractive = href !== undefined || onClick !== undefined;
  const hasOverflow = additional > 0;
  const isStacked = participants.length > 1;
  // Full participant count for screen readers — the `+N` text represents
  // `additional` real people, not one element.
  const total = participants.length + additional;

  const label = `${total} thread participant${total === 1 ? "" : "s"}`;

  // Shared by both the interactive and static renderings so the two can't drift
  // apart visually — the element changes, the appearance must not.
  const facepileClass = cn(
    "flex items-center transition-opacity",
    isInteractive ? "cursor-pointer hover:opacity-70" : "cursor-default",
    // Soft-chip container only when there's a `+N` count — single or
    // multi-avatar facepiles without overflow stay visually lighter.
    hasOverflow && "bg-accent rounded-full py-0.5 pl-0.5 pr-2",
    className,
  );

  const avatars = (
    <>
      {participants.map((p, i) => (
        // Descending z-index so leftmost stays on top of every avatar to its
        // right — with equal z-indices, later DOM order wins and the stacking
        // inverts past 2 avatars. AvatarWithFallback doesn't take `style`, so
        // wrap to apply it.
        <span
          key={p.handleId}
          className={cn("relative inline-flex", i > 0 && "-ml-3.5")}
          style={isStacked ? { zIndex: participants.length - i } : undefined}
        >
          <AvatarWithFallback
            size="xs"
            person={{ name: p.name, picture: p.picture, value: p.value ?? "" }}
            className={cn(isStacked ? "border border-popover" : "border-0")}
          />
        </span>
      ))}
      {hasOverflow && <span className="text-2xs ml-0.5">+{additional}</span>}
    </>
  );

  // A <button> here would be focusable wherever it's placed, so a host that is
  // itself a control — the Contacts row in the thread header's overflow menu —
  // would nest one interactive element inside another and strand keyboard
  // users on a target that does nothing. `role="img"` is what carries the
  // label; a bare <span> does not reliably expose an accessible name.
  if (!isInteractive) {
    return (
      <span className={facepileClass} role="img" aria-label={label}>
        {avatars}
      </span>
    );
  }

  if (href !== undefined) {
    return (
      <Link
        href={href}
        className={facepileClass}
        aria-label={label}
        // A host can be a clickable surface without being an anchor; keep the
        // facepile's own click from firing that too.
        onClick={(e) => e.stopPropagation()}
      >
        {avatars}
      </Link>
    );
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onClick?.();
  };

  return (
    <button type="button" onClick={handleClick} className={facepileClass} aria-label={label}>
      {avatars}
    </button>
  );
};
